import React, { useState } from 'react';
import { DatePicker, Input, OnboardingContainer } from '../../../components';
import {
  Content,
  Form,
  HeaderContainer,
  MoreQuestionsButton,
  QuestionnaireSection,
  SectionTitle,
  StyledButton,
  TitleContainer,
} from './WritingStylePage.styles';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectInfluencerRequestActionsPending,
  selectQuestionnaire,
} from '../../../store/slices/influencerRequest/slice';
import { Controller, useForm } from 'react-hook-form';
import { setUserStatus } from '../../../store/slices/user/slice';
import { notifyError } from '../../../utils/notify';
import { Text20Bold } from '../../../components/utils/Text/Text.styles';
import { saveWritingStyleAnswers } from '../../../store/slices/influencerRequest/asyncThunks';

const WritingStylePage = () => {
  const dispatch = useDispatch();
  const { saveWritingStyleAnswers: saveWritingStyleAnswersPending } = useSelector(
    selectInfluencerRequestActionsPending,
  );
  const questionnaire = useSelector(selectQuestionnaire);
  const [customQuestions, setCustomQuestions] = useState([]);

  const questionnaireFormSchema = yup
    .object()
    .shape({
      answers: yup.lazy(value => {
        const newEntries = Object.keys(value).reduce((acc, val) => {
          const [sectionIndex, questionIndex] = val.split('-');

          const question = questionnaire[sectionIndex].questions[questionIndex];

          if (question.required && question.type !== 'datepicker') {
            return {
              ...acc,
              [val]: yup.string().trim().required('Field is required'),
            };
          } else if (question.required && question.type === 'datepicker') {
            return {
              ...acc,
              [val]: yup
                .date()
                .required('Field is required')
                .max(
                  new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
                  'You must be at least 18 years old.',
                ),
            };
          } else {
            return acc;
          }
        }, {});

        return yup.object().shape(newEntries);
      }),
    })
    .required();

  const {
    register: questionnaireRegister,
    handleSubmit: questionnaireHandleSubmit,
    control,
    formState: { errors: questionnaireErrors },
  } = useForm({
    defaultValues: {
      answers: {},
    },
    delayError: 300,
    resolver: yupResolver(questionnaireFormSchema),
  });

  const onSubmitQuestionnaire = async data => {
    const questionnaireData = [];
    Object.entries(data.answers).forEach(([key, value]) => {
      const [sectionIndex, questionIndex] = key.split('-');
      const title = questionnaire[sectionIndex].title;
      const question = questionnaire[sectionIndex].questions[questionIndex];

      questionnaireData.push({
        title,
        question,
        answer: value,
      });
    });

    for (const [i, customQuestion] of customQuestions.entries()) {
      if (customQuestion.answer.trim()) {
        questionnaireData.push({
          title: 'Writing Style',
          question: { text: `Message ${i + 6}` },
          answer: customQuestion.answer,
        });
      }
    }

    dispatch(saveWritingStyleAnswers({ questionnaireData }))
      .unwrap()
      .then(({ userStatus }) => {
        dispatch(setUserStatus(userStatus));
      })
      .catch(err => {
        notifyError(err.message);
      });
  };

  return (
    <OnboardingContainer>
      <Content>
        <HeaderContainer>
          <Text20Bold>Personality Questions</Text20Bold>
        </HeaderContainer>
        <Form onSubmit={questionnaireHandleSubmit(onSubmitQuestionnaire)}>
          {questionnaire.map((question, index) => {
            return (
              question.questions.some(q => q.required) &&
              ['AI Limits', 'Writing Style'].includes(question.title) && (
                <QuestionnaireSection key={index}>
                  <TitleContainer>
                    <SectionTitle>{question.title}</SectionTitle>
                  </TitleContainer>
                  {question.questions.map((q, i) => {
                    return (
                      q.required &&
                      (q.type === 'datepicker' ? (
                        <Controller
                          key={i}
                          name={`answers[${index}-${i}]`}
                          defaultValue={null}
                          control={control}
                          render={({ field }) => {
                            return (
                              <DatePicker
                                label={q.text}
                                placeholder={q.placeholder || 'Your answer'}
                                value={field.value}
                                onChange={field.onChange}
                                error={questionnaireErrors.answers?.[`${index}-${i}`]?.message}
                              />
                            );
                          }}
                        />
                      ) : (
                        <Input
                          key={i}
                          label={q.text}
                          type={q.type || 'textarea'}
                          placeholder={q.placeholder || 'Your answer'}
                          {...questionnaireRegister(`answers[${index}-${i}]`)}
                          error={questionnaireErrors.answers?.[`${index}-${i}`]?.message}
                        />
                      ))
                    );
                  })}
                </QuestionnaireSection>
              )
            );
          })}
          <QuestionnaireSection>
            {customQuestions.map((q, i) => {
              return (
                <Input
                  key={i}
                  label={`Message ${i + 6}`}
                  value={q.answer}
                  onChange={e => {
                    const newQuestions = [...customQuestions];
                    newQuestions[i].answer = e.target.value;
                    setCustomQuestions(newQuestions);
                  }}
                  type="textarea"
                  placeholder="Enter your Answer"
                />
              );
            })}
          </QuestionnaireSection>
          <MoreQuestionsButton
            type="button"
            title="Add Example"
            onClick={() => setCustomQuestions(prev => [...prev, { answer: '' }])}
          />
          <StyledButton title="Continue" isLoading={saveWritingStyleAnswersPending} />
        </Form>
      </Content>
    </OnboardingContainer>
  );
};

export default WritingStylePage;
